import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
    Card, 
    Button, 
    Row, 
    Col, 
    message,
    Space,
    Typography,
    Spin,
    Statistic,
    Tag,
    Descriptions,
    Table,
    Progress,
    Badge
} from 'antd';
import { 
    ArrowLeftOutlined, 
    EditOutlined,
    PercentageOutlined,
    DollarOutlined,
    CalendarOutlined,
    UserOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    StopOutlined,
    BarChartOutlined,
    ShopOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { Coupon, CouponType, CouponStatus, CouponStats } from '../../../../types/coupon.types';
import { Company } from '../../../../types/company.types';
import couponService from '../../../../services/coupon/coupon.service';
import { productService } from '../../../../services/product/product.service';
import companyService from '../../../../services/company/company.service';
import { all_routes } from '../../../../router/all_routes';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface Product {
    id: number;
    name: string;
    durationMonths: number;
    pricePerUserPerMonth: number;
    isActive: boolean;
    description?: string;
    createdAt: string;
    updatedAt: string;
}

const CouponDetailsPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(false);
    const [coupon, setCoupon] = useState<Coupon | null>(null);
    const [stats, setStats] = useState<CouponStats | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);

    useEffect(() => {
        if (id) {
            fetchCouponDetails();
            fetchProducts();
            fetchCompanies();
        }
    }, [id]);

    const fetchProducts = async () => {
        try {
            const productList = await productService.getAllProducts();
            setProducts(productList);
        } catch (error) {
            console.error('Ürünler yüklenirken hata:', error);
        }
    };

    const fetchCompanies = async () => {
        try {
            const companyList = await companyService.getAllCompanies();
            setCompanies(companyList);
        } catch (error) {
            console.error('Şirketler yüklenirken hata:', error);
        }
    };

    const fetchCouponDetails = async () => {
        try {
            setLoading(true);
            const [couponData, statsData] = await Promise.all([
                couponService.getCoupon(Number(id)),
                fetchCouponStats()
            ]);
            setCoupon(couponData);
            setStats(statsData);
        } catch (error: any) {
            message.error('Kupon bilgileri yüklenirken hata oluştu');
            navigate(all_routes.coupons);
        } finally {
            setLoading(false);
        }
    };

    const fetchCouponStats = async () => {
        try {
            setStatsLoading(true);
            const statsData = await couponService.getCouponStats(Number(id));
            return statsData;
        } catch (error) {
            console.error('İstatistikler yüklenirken hata:', error);
            return null;
        } finally {
            setStatsLoading(false);
        }
    };

    const getStatusColor = (status: CouponStatus) => {
        switch (status) {
            case CouponStatus.ACTIVE: return 'success';
            case CouponStatus.INACTIVE: return 'default';
            case CouponStatus.EXPIRED: return 'error';
            default: return 'default';
        }
    };

    const getStatusText = (status: CouponStatus) => {
        switch (status) {
            case CouponStatus.ACTIVE: return 'Aktif';
            case CouponStatus.INACTIVE: return 'Pasif';
            case CouponStatus.EXPIRED: return 'Süresi Dolmuş';
            default: return status;
        }
    };

    const getStatusIcon = (status: CouponStatus) => {
        switch (status) {
            case CouponStatus.ACTIVE: return <CheckCircleOutlined />;
            case CouponStatus.INACTIVE: return <StopOutlined />;
            case CouponStatus.EXPIRED: return <ClockCircleOutlined />;
            default: return <StopOutlined />;
        }
    };

    const getTypeIcon = (type: CouponType) => {
        return type === CouponType.PERCENTAGE ? <PercentageOutlined /> : <DollarOutlined />;
    };

    const getTypeText = (type: CouponType) => {
        return type === CouponType.PERCENTAGE ? 'Yüzde İndirim' : 'Sabit Tutar İndirim';
    };

    const calculateUsagePercentage = () => {
        if (!coupon) return 0;
        if (coupon.usageLimit === 0) return 0; // Sınırsız kullanım
        return Math.round((coupon.usedCount / coupon.usageLimit) * 100);
    };

    const getApplicableProducts = () => {
        if (!coupon?.applicableProductIds || coupon.applicableProductIds.length === 0) {
            return 'Tüm Ürünler';
        }
        return coupon.applicableProductIds.map(id => {
            const product = products.find(p => p.id === id);
            return product ? product.name : `Ürün #${id}`;
        }).join(', ');
    };

    const getSpecificCompanies = () => {
        if (!coupon?.specificCompanyIds || coupon.specificCompanyIds.length === 0) {
            return 'Tüm Şirketler';
        }
        return coupon.specificCompanyIds.map(id => {
            const company = companies.find(c => c.id === id);
            return company ? company.name : `Şirket #${id}`;
        }).join(', ');
    };

    const usageHistoryColumns = [
        {
            title: 'Şirket ID',
            dataIndex: 'companyId',
            key: 'companyId',
            render: (companyId: number) => <Text strong>#{companyId}</Text>
        },
        {
            title: 'Kullanım Tarihi',
            dataIndex: 'usedAt',
            key: 'usedAt',
            render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm')
        },
        {
            title: 'Sipariş Tutarı',
            dataIndex: 'orderAmount',
            key: 'orderAmount',
            render: (amount: number) => `₺${amount.toFixed(2)}`
        },
        {
            title: 'İndirim Tutarı',
            dataIndex: 'discountAmount',
            key: 'discountAmount',
            render: (amount: number) => (
                <Text type="success" strong>₺{amount.toFixed(2)}</Text>
            )
        }
    ];

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    if (!coupon) {
        return (
            <div className="page-wrapper">
                <div className="content">
                    <div className="text-center">
                        <Title level={3}>Kupon Bulunamadı</Title>
                        <Button type="primary" onClick={() => navigate(all_routes.coupons)}>
                            Kupon Listesine Dön
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                        <Button 
                            type="text" 
                            icon={<ArrowLeftOutlined />} 
                            onClick={() => navigate(all_routes.coupons)}
                            className="me-2"
                        />
                        <div>
                            <h4 className="page-title mb-1">
                                <i className="ti ti-ticket me-2 text-primary"></i>
                                Kupon Detayları: {coupon.code}
                            </h4>
                            <p className="text-muted mb-0">Kupon bilgileri ve kullanım istatistikleri</p>
                        </div>
                    </div>
                    <Space>
                        <Link to={`${all_routes.coupons}/${id}/edit`}>
                            <Button type="primary" icon={<EditOutlined />}>
                                Düzenle
                            </Button>
                        </Link>
                    </Space>
                </div>

                <Row gutter={24}>
                    {/* Sol Kolon - Kupon Bilgileri */}
                    <Col xs={24} lg={16}>
                        {/* Kupon Kartı */}
                        <Card className="border-0 shadow-sm mb-4">
                            <div className="text-center">
                                <div className="p-4 border rounded bg-gradient mb-4" style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white'
                                }}>
                                    <div className="d-flex align-items-center justify-content-center mb-2">
                                        <div className="me-2 fs-3">
                                            {getTypeIcon(coupon.type)}
                                        </div>
                                        <div className="fs-2 fw-bold">
                                            {coupon.code}
                                        </div>
                                    </div>
                                    <div className="fs-5 mb-3">{coupon.name}</div>
                                    <div className="fs-1 fw-bold mb-2">
                                        {coupon.type === CouponType.PERCENTAGE 
                                            ? `%${coupon.value}` 
                                            : `₺${coupon.value}`
                                        }
                                    </div>
                                    <div className="small opacity-75 mb-3">
                                        {getTypeText(coupon.type)}
                                    </div>
                                    
                                    {/* Durum Badge */}
                                    <div>
                                        <Badge 
                                            status={getStatusColor(coupon.status) as any}
                                            text={
                                                <span className="text-white fw-bold">
                                                    {getStatusIcon(coupon.status)} {getStatusText(coupon.status)}
                                                </span>
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Kupon Detayları */}
                        <Card title="Kupon Bilgileri" className="border-0 shadow-sm mb-4">
                            <Descriptions column={2} bordered>
                                <Descriptions.Item label="Kupon Kodu" span={2}>
                                    <Text strong className="text-primary">{coupon.code}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Kupon Adı" span={2}>
                                    {coupon.name}
                                </Descriptions.Item>
                                {coupon.description && (
                                    <Descriptions.Item label="Açıklama" span={2}>
                                        {coupon.description}
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label="İndirim Tipi">
                                    <Space>
                                        {getTypeIcon(coupon.type)}
                                        {getTypeText(coupon.type)}
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="İndirim Değeri">
                                    <Text strong className="text-success">
                                        {coupon.type === CouponType.PERCENTAGE 
                                            ? `%${coupon.value}` 
                                            : `₺${coupon.value}`
                                        }
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Toplam Kullanım Limiti">
                                    {coupon.usageLimit === 0 ? 'Sınırsız' : coupon.usageLimit.toLocaleString()}
                                </Descriptions.Item>
                                <Descriptions.Item label="Şirket Başına Limit">
                                    {coupon.companyUsageLimit}
                                </Descriptions.Item>
                                {coupon.minUserCount && (
                                    <Descriptions.Item label="Minimum Kullanıcı Sayısı" span={2}>
                                        <Tag color="blue" icon={<UserOutlined />}>
                                            {coupon.minUserCount} kullanıcı
                                        </Tag>
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label="Geçerli Ürünler" span={2}>
                                    <Space wrap>
                                        <ShopOutlined />
                                        <Text>{getApplicableProducts()}</Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Geçerli Şirketler" span={2}>
                                    <Space wrap>
                                        <TeamOutlined />
                                        <Text>{getSpecificCompanies()}</Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Başlangıç Tarihi">
                                    <Space>
                                        <CalendarOutlined />
                                        {dayjs(coupon.startDate).format('DD.MM.YYYY')}
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Bitiş Tarihi">
                                    <Space>
                                        <CalendarOutlined />
                                        {dayjs(coupon.endDate).format('DD.MM.YYYY')}
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Oluşturulma Tarihi">
                                    {dayjs(coupon.createdAt).format('DD.MM.YYYY HH:mm')}
                                </Descriptions.Item>
                                <Descriptions.Item label="Son Güncelleme">
                                    {dayjs(coupon.updatedAt).format('DD.MM.YYYY HH:mm')}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>

                        {/* Kullanım Geçmişi */}
                        {coupon.usageHistory && coupon.usageHistory.length > 0 && (
                            <Card title="Kullanım Geçmişi" className="border-0 shadow-sm">
                                <Table
                                    columns={usageHistoryColumns}
                                    dataSource={coupon.usageHistory}
                                    rowKey={(record, index) => `usage-${index}`}
                                    pagination={{
                                        pageSize: 10,
                                        showTotal: (total, range) => 
                                            `${range[0]}-${range[1]} / ${total} kullanım`
                                    }}
                                    scroll={{ x: 600 }}
                                />
                            </Card>
                        )}
                    </Col>

                    {/* Sağ Kolon - İstatistikler */}
                    <Col xs={24} lg={8}>
                        {/* Kullanım İstatistikleri */}
                        <Card 
                            title={
                                <Space>
                                    <BarChartOutlined />
                                    Kullanım İstatistikleri
                                </Space>
                            } 
                            className="border-0 shadow-sm mb-4"
                            loading={statsLoading}
                        >
                            <Row gutter={16}>
                                <Col span={24} className="mb-3">
                                    <Statistic
                                        title="Toplam Kullanım"
                                        value={coupon.usedCount}
                                        suffix={`/ ${coupon.usageLimit === 0 ? '∞' : coupon.usageLimit}`}
                                        prefix={<UserOutlined />}
                                    />
                                    {coupon.usageLimit > 0 && (
                                        <Progress 
                                            percent={calculateUsagePercentage()} 
                                            strokeColor="#52c41a"
                                            className="mt-2"
                                        />
                                    )}
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Card className="text-center border-0 bg-light">
                                        <Statistic
                                            title="Kalan Kullanım"
                                            value={coupon.usageLimit === 0 ? '∞' : Math.max(0, coupon.usageLimit - coupon.usedCount)}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card className="text-center border-0 bg-light">
                                        <Statistic
                                            title="Kullanım Oranı"
                                            value={calculateUsagePercentage()}
                                            suffix="%"
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </Card>

                        {/* Genel İstatistikler */}
                        {stats && (
                            <Card 
                                title="Genel İstatistikler" 
                                className="border-0 shadow-sm mb-4"
                            >
                                <Space direction="vertical" className="w-100">
                                    <Statistic
                                        title="Toplam İndirim Verilen"
                                        value={stats.totalDiscountGiven}
                                        prefix="₺"
                                        precision={2}
                                        valueStyle={{ color: '#52c41a' }}
                                    />
                                    <Statistic
                                        title="Bu Ayın Kullanımı"
                                        value={coupon.usedCount}
                                        valueStyle={{ color: '#1890ff' }}
                                    />
                                </Space>
                            </Card>
                        )}

                        {/* Durum Kartı */}
                        <Card className="border-0 shadow-sm">
                            <div className="text-center">
                                <div className="mb-3">
                                    <Tag 
                                        color={getStatusColor(coupon.status)} 
                                        className="px-3 py-2 fs-6"
                                        icon={getStatusIcon(coupon.status)}
                                    >
                                        {getStatusText(coupon.status)}
                                    </Tag>
                                </div>
                                
                                {/* Durum Açıklamaları */}
                                {coupon.status === CouponStatus.ACTIVE && (
                                    <Text type="success">
                                        Kupon aktif durumda ve kullanılabilir
                                    </Text>
                                )}
                                {coupon.status === CouponStatus.INACTIVE && (
                                    <Text type="secondary">
                                        Kupon pasif durumda
                                    </Text>
                                )}
                                {coupon.status === CouponStatus.EXPIRED && (
                                    <Text type="danger">
                                        Kupon süresi dolmuş
                                    </Text>
                                )}

                                {/* Tarih Kontrolü */}
                                <div className="mt-3">
                                    {dayjs().isBefore(dayjs(coupon.startDate)) && (
                                        <Text type="warning" className="small">
                                            Kupon henüz başlamamış
                                        </Text>
                                    )}
                                    {dayjs().isAfter(dayjs(coupon.endDate)) && (
                                        <Text type="danger" className="small">
                                            Kupon süresi dolmuş
                                        </Text>
                                    )}
                                    {dayjs().isBetween(dayjs(coupon.startDate), dayjs(coupon.endDate)) && (
                                        <Text type="success" className="small">
                                            Kupon geçerlilik süresinde
                                        </Text>
                                    )}
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default CouponDetailsPage; 